import { CreditCard, Landmark } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUpdateAdminLocation } from '@/hooks/use-admin';
import { toast } from '@/stores/toast-store';

/**
 * The two payment method switches that sit under the Receive Payments module
 * gate on a super-admin location card: Credit / Debit Card and ACH / Bank
 * Account. Each is a location-level flag (locations.rp_card_enabled /
 * rp_ach_enabled); the merchant's Receive Payments → New Sale tabs lock on
 * them. Default ON, so turning one off is always a deliberate choice.
 */
export interface ReceivePaymentMethodSwitchesProps {
  tenantId: string;
  location: { id: string; rpCardEnabled?: boolean | null; rpAchEnabled?: boolean | null };
}

const METHODS = [
  {
    key: 'rpCardEnabled',
    label: 'Credit / Debit Card',
    icon: CreditCard,
    onCopy: 'Merchant can take card payments on New Sale',
    offCopy: 'Card tab is locked on New Sale at this location',
  },
  {
    key: 'rpAchEnabled',
    label: 'ACH / Bank Account',
    icon: Landmark,
    onCopy: 'Merchant can take ACH payments on New Sale',
    offCopy: 'ACH tab is locked on New Sale at this location',
  },
] as const;

export function ReceivePaymentMethodSwitches({ tenantId, location }: ReceivePaymentMethodSwitchesProps) {
  const update = useUpdateAdminLocation();

  return (
    <div className="mt-3 space-y-2 border-t border-border/60 pt-3" data-testid="receive-payment-method-switches">
      <p className="text-[11px] text-muted-foreground">
        Payment methods the business may take on New Sale at this location.
      </p>
      {METHODS.map(({ key, label, icon: Icon, onCopy, offCopy }) => {
        // A row from before the switches existed has no value yet: treat it as
        // ON, matching the column default and what the merchant side resolves.
        const enabled = location[key] !== false;
        const onToggle = () => {
          const next = !enabled;
          update.mutate(
            { tenantId, locationId: location.id, [key]: next },
            {
              onSuccess: () => toast.success(`${label} ${next ? 'enabled' : 'disabled'} for this location`),
              onError: (err: unknown) => toast.error(err instanceof Error ? err.message : `Failed to update ${label}`),
            },
          );
        };
        return (
          <div key={key} className="flex items-center justify-between rounded-md bg-white/60 px-2 py-1.5">
            <div className="flex min-w-0 items-center gap-2">
              <Icon className="h-3.5 w-3.5 text-primary" />
              <div className="min-w-0">
                <div className="text-xs font-medium">{label}</div>
                <div className="text-[11px] text-muted-foreground">
                  {enabled ? <span className="text-green-600">{onCopy}</span> : <span>{offCopy}</span>}
                </div>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label={label}
              title={enabled ? `Disable ${label} at this location` : `Enable ${label} at this location`}
              onClick={onToggle}
              disabled={update.isPending}
              className={cn(
                'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                enabled ? 'bg-primary' : 'bg-gray-200',
              )}
            >
              <span
                className={cn(
                  'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                  enabled ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </button>
          </div>
        );
      })}
    </div>
  );
}
